/**
 * The 2FA enrollment gate (standing policy: two-factor, always).
 * A signed-in user with no enrolled factor lands here before anything
 * else: scan the QR (or type the secret) into an authenticator app,
 * confirm one code, and the account is enrolled. Once every user is
 * enrolled, the server side flips ENABLED -> MANDATORY and this gate
 * becomes belt-and-braces.
 */
import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import type { TotpSecret } from 'firebase/auth';
import { ActionButton, DoorCard, Field, Mono, Notice, Stack, Text, Title } from '../../../strata';
import { finishTotpEnrollment, signOutUser, type User } from '../session';
import { startTotpEnrollment } from '../session';

interface TotpEnrollProps {
  user: User;
  onEnrolled: () => void;
}

export function TotpEnroll({ user, onEnrolled }: TotpEnrollProps) {
  const [secret, setSecret] = useState<TotpSecret | null>(null);
  const [secretKey, setSecretKey] = useState<string>('');
  const [qrDataUrl, setQrDataUrl] = useState<string>('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Shared across the enrollment effect and confirm(): true whenever this mount is the one
  // an in-flight promise was started under, so a stale resolution (unmount, or a re-run of the
  // effect on a new `user`) never lands state on a generation that isn't its own.
  const live = useRef(true);

  useEffect(() => {
    live.current = true;
    startTotpEnrollment(user)
      .then(async ({ secret, otpauthUri }) => {
        if (!live.current) return;
        setSecret(secret);
        setSecretKey(secret.secretKey);
        const dataUrl = await QRCode.toDataURL(otpauthUri, { width: 200, margin: 1 });
        if (!live.current) return;
        setQrDataUrl(dataUrl);
      })
      .catch((err) => {
        if (!live.current) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      live.current = false;
    };
  }, [user]);

  const confirm = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!secret) return;
    setBusy(true);
    setError(null);
    try {
      await finishTotpEnrollment(user, secret, code.trim());
      if (!live.current) return;
      onEnrolled();
    } catch (err) {
      if (!live.current) return;
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (live.current) setBusy(false);
    }
  };

  return (
    <DoorCard>
      <Stack direction="column" gap="md">
        <Title level={3}>Set up two-factor</Title>
        <Text tone="secondary" size="control">
          Polywog requires a second factor. Scan with your authenticator app (1Password, Google Authenticator, ...),
          then confirm a code.
        </Text>
        <Stack direction="column" gap="sm" align="center">
          {qrDataUrl ? (
            <img src={qrDataUrl} alt="TOTP enrollment QR" width={200} height={200} />
          ) : (
            <Text tone="muted" size="control">
              Generating…
            </Text>
          )}
        </Stack>
        {secretKey && (
          <Text tone="muted" size="micro" breakAll>
            Manual key: <Mono tone="muted" size="micro">{secretKey}</Mono>
          </Text>
        )}
        <form onSubmit={(e) => void confirm(e)}>
          <Stack direction="column" gap="sm">
            <Field
              label="6-digit code"
              labelHidden
              inputMode="numeric"
              pattern="[0-9]*"
              isRequired
              mono
              placeholder="6-digit code"
              value={code}
              onChange={setCode}
            />
            <ActionButton variant="brand" type="submit" isDisabled={busy || !secret} isPending={busy}>
              {busy ? 'Confirming…' : 'Confirm'}
            </ActionButton>
          </Stack>
        </form>
        {error && <Notice kind="error">{error}</Notice>}
        <ActionButton size="sm" onPress={() => void signOutUser()}>
          Sign out
        </ActionButton>
      </Stack>
    </DoorCard>
  );
}
